/**
 * PalletQuickLogDialog — minimal inline dialog for logging a pallet
 * movement against an existing business event (a Receipt or a Shipment).
 *
 * Two-tap UX: pick type, enter qty, hit save. Tags the movement with the
 * source's merchant_id, sets `reference` to the source's number, and uses
 * `kind='in'` for receipts and `kind='out'` for despatch so the balance
 * aggregation gets the right sign. Auto-loads pallet types from the API
 * the first time it opens.
 */
import React, { useEffect, useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { toast } from 'sonner';
import { Stack } from '@phosphor-icons/react';
import { listPalletTypes, createPalletMovement, seedPalletDefaults } from '../../lib/api';

export default function PalletQuickLogDialog({
  open, setOpen,
  // Source context — receipt or shipment row.
  source = 'receipt',          // 'receipt' | 'despatch'
  reference,                    // e.g. receipt_number, shipment_id
  merchantId,                   // optional — auto-tag
  warehouseId,                  // optional
  onLogged,
}) {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ type_id: '', qty: 1, condition: '', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Tenants may have skipped the /pallets page entirely — seed
        // defaults so the dialog always shows three valid options.
        await seedPalletDefaults().catch(() => null);
        const r = await listPalletTypes();
        if (cancelled) return;
        const active = (r.data || []).filter((t) => t.is_active);
        setTypes(active);
        // Pick the most likely type as the default — Wooden for owned, CHEP for pooled.
        const def = active.find((t) => t.code === 'WOOD') || active[0];
        setForm({ type_id: def?.id || '', qty: 1, condition: '', notes: '' });
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Could not load pallet types');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const submit = async () => {
    if (!form.type_id) return toast.error('Pick a pallet type');
    if (!Number(form.qty) || Number(form.qty) <= 0) return toast.error('Quantity must be > 0');
    setSaving(true);
    try {
      const payload = {
        type_id: form.type_id,
        kind: source === 'despatch' ? 'out' : 'in',
        qty: Number(form.qty),
      };
      if (reference) payload.reference = String(reference);
      if (merchantId) payload.merchant_id = merchantId;
      if (warehouseId) payload.warehouse_id = warehouseId;
      if (form.condition) payload.condition = form.condition;
      if (form.notes && form.notes.trim()) payload.notes = form.notes.trim();
      await createPalletMovement(payload);
      toast.success(`Logged ${form.qty} pallet(s)`);
      setOpen(false);
      onLogged && onLogged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const directionLabel = source === 'despatch' ? 'OUT (despatched)' : 'IN (received)';

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md" data-testid="pallet-quick-log-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Stack size={18} /> Log pallets · {directionLabel}
          </DialogTitle>
          <DialogDescription>
            Posts a {source === 'despatch' ? 'despatch' : 'receipt'}-tagged pallet movement —
            balances and the daily hire scheduler will pick it up automatically.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="p-4 text-center text-muted-foreground text-sm">Loading types…</div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label>Pallet type *</Label>
              <select className="w-full border border-border bg-background h-9 px-2 text-sm"
                value={form.type_id} onChange={(e) => setForm({ ...form, type_id: e.target.value })}
                data-testid="pallet-quick-log-type">
                {types.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.code} · {t.name}{t.is_pooled ? ' (pooled)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Qty *</Label>
                <Input type="number" min="1" value={form.qty}
                  onChange={(e) => setForm({ ...form, qty: e.target.value })}
                  data-testid="pallet-quick-log-qty" />
              </div>
              <div>
                <Label>Condition</Label>
                <select className="w-full border border-border bg-background h-9 px-2 text-sm"
                  value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })}
                  data-testid="pallet-quick-log-condition">
                  <option value="">—</option>
                  <option value="A">A · Good</option>
                  <option value="B">B · Fair</option>
                  <option value="C">C · Damaged</option>
                </select>
              </div>
            </div>
            {reference && (
              <div className="text-[11px] text-muted-foreground bg-muted/30 border border-border p-2">
                Tagged with reference <strong className="font-mono">{reference}</strong>
                {merchantId && ' · merchant on file'}
              </div>
            )}
            <div>
              <Label>Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="pallet-quick-log-notes" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || loading} data-testid="pallet-quick-log-submit">
            {saving ? 'Saving…' : 'Log pallets'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
